import React from 'react';
import { motion } from 'framer-motion';
import { 
  AcademicCapIcon,
  BellIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../contexts/SiteBrandingContext';
import TranslatableText from './TranslatableText';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const { isDark } = useTheme();
  const { siteBranding } = useSiteBranding();

  return (
    <div className={`${isDark ? 'bg-dark-bg' : 'bg-white'} py-4 border-b ${isDark ? 'border-dark-border' : 'border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo and School Info */}
          <div className="flex items-center space-x-4">
            {/* Logo */}
            <motion.div 
              className="flex-shrink-0"
              whileHover={{ scale: 1.05 }}
            >
              {siteBranding.logo_url ? (
                <img 
                  src={siteBranding.logo_url} 
                  alt={siteBranding.site_name}
                  className="w-16 h-16 object-contain rounded-lg"
                  style={{ maxWidth: '100%', height: 'auto' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
              ) : (
                <img 
                  src="https://i.postimg.cc/bNdvMYR5/pm-shri-seeklogo.png" 
                  alt="PM SHRI Logo"
                  className="w-16 h-16 object-contain rounded-lg"
                  style={{ maxWidth: '100%', height: 'auto' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
              )}
              
              {/* Fallback icon - only shown when image fails to load */}
              <div 
                className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full hidden items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${siteBranding.primary_color}, ${siteBranding.secondary_color})`
                }}
              >
                <AcademicCapIcon className="w-8 h-8 text-white" />
              </div>
            </motion.div>

            {/* School Name and Details */}
            <div>
              <h1 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'}`}>
                <TranslatableText text={siteBranding.site_name} />
              </h1>
              <p className="text-sm md:text-base" style={{ color: siteBranding.primary_color }}>
                <TranslatableText text={siteBranding.site_tagline} />
              </p>
              <div className="flex items-center space-x-4 mt-1">
                <div className="flex items-center space-x-1">
                  <StarIcon className="w-4 h-4 text-yellow-500" />
                  <span className={`text-xs ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                    <TranslatableText text="PM SHRI Status" />
                  </span>
                </div>
                <div className={`text-xs ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
                  <TranslatableText text="Affiliation No: 520354" />
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Theme toggle and Notice */}
          <div className="flex items-center space-x-4">
            <ThemeToggle />
            
            {/* Notice Board */}
            <motion.div 
              className={`hidden lg:flex items-center space-x-2 ${isDark ? 'bg-red-900/30 border-red-500/50' : 'bg-red-50 border-red-200'} px-3 py-2 rounded-lg border`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <BellIcon className="w-4 h-4 text-red-600" />
              <div>
                <div className="text-xs font-semibold text-red-800">
                  <TranslatableText text="Latest Notice" />
                </div>
                <div className={`text-xs ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                  <TranslatableText text="Admissions Open for 2026-27" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;